/**
 * RSVP planner tools — built around the "chase pending guests" loop:
 *
 * - `list_pending_rsvps` reports who still hasn't responded and whether each
 *   one already has a public RSVP link the couple can send.
 * - `ensure_rsvp_link` mints (or returns the existing) link for one guest.
 *
 * Bulk reads ("show me all guests") already work through `list_guests`; these
 * are the dedicated RSVP-funnel tools the model reaches for when the user
 * says "remind everyone who hasn't replied" or "send Anna her RSVP link".
 */

import { Schema } from "firebase/ai";
import { isWeddingCoupleGuest } from "../guests/guest-unit-rows";
import type { PlannerTool } from "./registry";
import { asBoolean, asString, requireString } from "./helpers";

export const rsvpTools: PlannerTool[] = [
  {
    declaration: {
      name: "list_pending_rsvps",
      description:
        "List guests whose RSVP is still 'pending' and whether each one already has a public RSVP link to send. Use this when the couple asks who hasn't responded, or wants to chase pending guests. Excludes the bride and groom rows.",
      parameters: Schema.object({
        properties: {
          onlyWithoutLink: Schema.boolean({
            description:
              "When true, only return pending guests that don't yet have an RSVP link generated.",
          }),
        },
        optionalProperties: ["onlyWithoutLink"],
      }),
    },
    destructive: false,
    describe: () => "Lista oczekujących RSVP",
    execute: async (args, ctx) => {
      const onlyWithoutLink = asBoolean(args.onlyWithoutLink) ?? false;
      const guests = ctx
        .guests()
        .filter((g) => g.rsvpStatus === "pending" && !isWeddingCoupleGuest(g));
      const rows = guests
        .map((g) => {
          const token = g.rsvpToken ?? null;
          return {
            id: g.id,
            name: `${g.firstName} ${g.lastName}`.trim(),
            phone: g.phone ?? null,
            hasLink: token !== null,
            link: token ? ctx.operations.rsvp.rsvpLinkUrl(token) : null,
          };
        })
        .filter((r) => (onlyWithoutLink ? !r.hasLink : true));
      return {
        count: rows.length,
        totalPending: guests.length,
        guests: rows,
      };
    },
  },
  {
    declaration: {
      name: "ensure_rsvp_link",
      description:
        "Generate or return the existing public RSVP link for a single guest. Idempotent: a guest already has at most one token. Resolve guestId via list_guests or list_pending_rsvps first.",
      parameters: Schema.object({
        properties: {
          guestId: Schema.string({ description: "Exact guest ID." }),
        },
      }),
    },
    destructive: false,
    describe: (args, ctx) => {
      const id = asString(args.guestId);
      if (!id) return "Link RSVP";
      const g = ctx.guests().find((x) => x.id === id);
      const name = g ? `${g.firstName} ${g.lastName}`.trim() : id;
      return `Link RSVP: ${name}`;
    },
    execute: async (args, ctx) => {
      const guestId = requireString(args, "guestId");
      const guest = ctx.guests().find((g) => g.id === guestId);
      if (!guest) {
        throw new Error(`No guest with ID "${guestId}". Call list_guests first.`);
      }
      if (isWeddingCoupleGuest(guest)) {
        throw new Error("The bride and groom rows don't get RSVP links.");
      }
      const url = await ctx.operations.rsvp.ensureRsvpLink(ctx.wedding(), guest);
      return { ok: true, guestId, name: `${guest.firstName} ${guest.lastName}`.trim(), link: url };
    },
  },
];
